import { TRACKING_RE } from './types';

/**
 * Normalize an OCR fragment into an uppercase alphanumeric string:
 * remove spaces, line breaks, hyphens, dots and any other separators.
 */
export function normalizeTracking(raw: string | null | undefined): string {
  return (raw ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/**
 * Apply UPS-prefix corrections that are safe because the target format
 * (`^1Z[A-Z0-9]{16}$`) is known:
 *  - "12Y..."  -> "1ZY..."   (OCR often reads Z as 2)
 *  - "1Z2Y2..."-> "1ZY2..."  (OCR sometimes swaps Y/2 after the prefix)
 * Returns the corrected string. Corrections are only applied when they
 * make the candidate valid; otherwise the original is returned.
 */
export function correctTracking(candidate: string): {
  value: string;
  corrected: boolean;
} {
  let value = candidate;
  let corrected = false;
  const candidates = [value];

  if (value.startsWith('12Y')) {
    const fixed = `1Z${value.slice(2)}`;
    candidates.push(fixed);
  } else if (value.startsWith('1Z2Y2')) {
    const fixed = `1ZY${value.slice(4)}`;
    candidates.push(fixed);
  }

  for (const c of candidates) {
    if (TRACKING_RE.test(c)) {
      if (c !== candidate) corrected = true;
      value = c;
      break;
    }
  }
  return { value, corrected };
}

/**
 * Validate and normalize a raw tracking OCR string.
 * Returns null when the candidate does not conform to the UPS format.
 */
export function parseTracking(raw: string | null | undefined): {
  value: string | null;
  corrected: boolean;
} {
  const normalized = normalizeTracking(raw);
  if (!normalized) return { value: null, corrected: false };
  const { value, corrected } = correctTracking(normalized);
  if (!TRACKING_RE.test(value)) return { value: null, corrected: false };
  return { value, corrected };
}

/** Extract every plausible tracking candidate (1Z + 16 alphanumeric) from a raw string. */
export function extractTrackingCandidates(raw: string | null | undefined): string[] {
  const normalized = normalizeTracking(raw);
  if (!normalized) return [];
  const out: string[] = [];
  const re = /1Z[A-Z0-9]{16}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(normalized)) !== null) {
    out.push(m[0]);
  }
  return out;
}

/** Normalize a PO / Trx Ref number. */
export function normalizePO(raw: string | null | undefined): string | null {
  const t = (raw ?? '').trim().toUpperCase();
  if (!t) return null;
  const m = t.match(/TRX\s*REF\s*NO\.?\s*:?\s*([A-Z0-9]{6,})/i);
  if (m) return m[1];
  const m2 = t.match(/[A-Z0-9]{6,}/);
  return m2 ? m2[0] : null;
}

/**
 * Sanitize a value for use as a file name: replace characters that are not
 * safe on common file systems with `_`.
 */
export function sanitizeFileName(value: string): string {
  const sanitized = value
    .normalize('NFKC')
    .replace(/[<>:"/\\|?*\u0000-\u001F]/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
  return sanitized || 'untitled';
}

/** Make a unique file name, appending _2, _3 ... on collisions. */
export function uniqueFileName(desired: string, taken: Set<string>): string {
  if (!taken.has(desired)) return desired;
  const dot = desired.lastIndexOf('.');
  const stem = dot > 0 ? desired.slice(0, dot) : desired;
  const ext = dot > 0 ? desired.slice(dot) : '';
  for (let i = 2; ; i += 1) {
    const candidate = `${stem}_${i}${ext}`;
    if (!taken.has(candidate)) return candidate;
  }
}

/** Strip a trailing ".pdf" (case-insensitive) from a source file name. */
export function sourceBaseName(fileName: string): string {
  return fileName.replace(/\.pdf$/i, '');
}
